/**
 * @description
 * VSCode Lens service for managing tables and data streaming.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { getAuthToken } from "../../../lib/auth";
import { BaseAPIClient } from "../core/base-client";
import type {
  TableInfo,
  FilterConfig,
} from "../../../types/vscode-lens";

/**
 * VSCode Lens service for managing tables and streaming
 */
export class VSCodeLensService extends BaseAPIClient {
  /**
   * Get available VSCode Lens tables
   */
  async getVSCodeLensTables(): Promise<TableInfo[]> {
    try {
      const data = await this.request<{ tables: TableInfo[] }>(
        "/vscode-lens/tables",
      );
      return data.tables;
    } catch (error) {
      // Handle configuration errors gracefully
      if (error instanceof Error && error.message.includes("not configured")) {
        throw new Error(
          "VSCodeLens is not configured. Please configure API key and URL in Settings.",
        );
      }
      throw error;
    }
  }

  /**
   * Create a VSCode Lens data stream
   */
  async createVSCodeLensStream(
    table: string,
    filters: FilterConfig,
  ): Promise<Response> {
    const token = getAuthToken();
    const params = new URLSearchParams();

    params.set("table", table);

    // Only send essential filters for initial load: time range and batch size
    if (filters.timePreset) {
      params.set("timePreset", filters.timePreset);
    }
    if (filters.startTime) {
      params.set("startTime", filters.startTime);
    }
    if (filters.endTime) {
      params.set("endTime", filters.endTime);
    }
    if (filters.batchSize) {
      params.set("batchSize", filters.batchSize.toString());
    }

    // Skip column filters for initial load - they will be applied on frontend
    // This makes the initial stream more efficient
    // Column filtering will be handled client-side after data is received

    return fetch(
      `${this.baseURL}/vscode-lens/stream/data?${params.toString()}`,
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      },
    );
  }
}

// Export singleton instance
export const vscodeLensService = new VSCodeLensService();
